import { useTranslation } from 'react-i18next';
import { UseHeadInput } from 'unhead/types';
import { useHead } from '@unhead/react';
import { CreateClientHeadOptions, createHead } from '@unhead/react/client';

export type AlternateLinks = Record<string, string | undefined>;

export type ToverHeadOptions = {
  title: string;
  description?: string;
  alternateLinks?: AlternateLinks;
  appName?: string;
};

export type CreateToverHeadOptions = CreateClientHeadOptions & { appName?: string };

let configuredAppName = '';

export function configureToverHead({ appName }: { appName: string }): void {
  configuredAppName = appName;
}

// Sets up unhead and the app-name suffix in one go: const head = createToverHead({ appName });
// then wrap the app root in <UnheadProvider value={head}>.
export function createToverHead({ appName = '', ...options }: CreateToverHeadOptions = {}) {
  configureToverHead({ appName });
  return createHead(options);
}

// English is always the canonical version — search engines get one stable address per
// piece of content instead of whichever locale the reader happened to land on.
function canonicalHref(alternateLinks?: AlternateLinks): string {
  const englishLocale = Object.keys(alternateLinks ?? {}).find((locale) => locale.toLowerCase().startsWith('en'));
  return (englishLocale && alternateLinks?.[englishLocale]) || window.location.pathname;
}

export function useToverHead({
  title,
  description,
  alternateLinks,
  appName = configuredAppName,
}: ToverHeadOptions): void {
  const { t, i18n } = useTranslation();

  useHead({
    // A title is either a lang key or already the thing to show — an article's own
    // title, say — so only what the catalogue knows gets translated.
    title: (i18n.exists(title) ? t(title) : title) as string,
    titleTemplate: appName ? `%s | ${appName}` : '%s',
    meta: description ? [{ name: 'description', content: description }] : [],
    link: [
      { rel: 'canonical', href: canonicalHref(alternateLinks) },
      ...Object.entries(alternateLinks ?? {})
        .filter((entry): entry is [string, string] => !!entry[1])
        .map(([hreflang, href]) => ({ rel: 'alternate', hreflang, href })),
    ] as UseHeadInput['link'],
  });
}

export default useToverHead;
